import React, { createContext, useContext, useState } from 'react'
import { UserModal } from './Modals/UserModal'
import { GroupModal } from './Modals/GroupModal'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import { IConfigRetailUser } from 'constants/interfaces'
import { deleteUser } from 'redux/actions/admin'
import { useDispatch } from 'react-redux'
import { Portal } from '@chakra-ui/core'
import { useToast } from '@chakra-ui/core'
import { AppDispatch } from 'config/redux'
import useLocaleText from 'components/useLocaleText'

interface UserModalsContextType {
  openUserModal: (user?: IConfigRetailUser) => void
  openGroupModal: (user: IConfigRetailUser) => void
  requestClose: (modal: 'user' | 'group') => void
  requestDeleteUser: (user: IConfigRetailUser) => void
}

const UserModalsContext = createContext<UserModalsContextType | null>(null)

export const useUserModals = () => {
  const ctx = useContext(UserModalsContext)
  if (!ctx) throw new Error('useUserModals must be used within UserModalsProvider')
  return ctx
}

export const UserModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()

  const [userModalOpen, setUserModalOpen] = useState(false)
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

  const [selectedUser, setSelectedUser] = useState<IConfigRetailUser | null>(null)
  const [selectedUid, setSelectedUid] = useState<string | null>(null)
  const [activeModal, setActiveModal] = useState<'user' | 'group' | null>(null)
  const [isDeleteUserLoading, setIsDeleteUserLoading] = useState(false)

  const admin_cancel_confirm = useLocaleText('admin_cancel_confirm')
  const admin_cancel_desc = useLocaleText('card_confirm_desc')
  const admin_no_cancel = useLocaleText('no')
  const admin_yes_cancel_button = useLocaleText('yes')

  const admin_delete_user_confirm = useLocaleText('admin_delete_user_confirm ')
  const admin_delete_user_success = useLocaleText('admin_delete_user_success')

  const openUserModal = (user?: IConfigRetailUser) => {
    setSelectedUser(user || null)
    setActiveModal('user')
    setUserModalOpen(true)
  }

  const openGroupModal = ({ uid }: { uid: string }) => {
    setSelectedUid(uid)
    setActiveModal('group')
    setGroupModalOpen(true)
  }

  const onClose = () => {
    setActiveModal(null)
    setUserModalOpen(false)
    setGroupModalOpen(false)
    setConfirmationOpen(false)
  }

  const requestClose = (modal: 'user' | 'group') => {
    setActiveModal(modal)
    setConfirmationOpen(true)
  }

  const confirmClose = () => {
    if (activeModal === 'user') setUserModalOpen(false)
    if (activeModal === 'group') setGroupModalOpen(false)
    setConfirmationOpen(false)
    setSelectedUser(null)
  }

  const cancelClose = () => setConfirmationOpen(false)

  const requestDeleteUser = (user: IConfigRetailUser) => {
    setSelectedUser(user)
    setDeleteConfirmationOpen(true)
  }

  const confirmDelete = async () => {
    if (selectedUser) {
      try {
        setIsDeleteUserLoading(true)
        await dispatch(deleteUser([selectedUser.uid]))
        if (activeModal === 'user') setUserModalOpen(false)
        if (activeModal === 'group') setGroupModalOpen(false)
        setSelectedUser(null)
        setDeleteConfirmationOpen(false)
        toast({
          title: admin_delete_user_success,
          status: 'success',
          position: 'top-right',
        })
      } catch (error) {
        console.error('[confirmDelete] Error:', error)
        toast({
          title: 'Error',
          description: 'Failed to delete user',
        })
      } finally {
        setIsDeleteUserLoading(false)
      }
    }
  }

  const cancelDelete = () => setDeleteConfirmationOpen(false)

  return (
    <UserModalsContext.Provider value={{ openUserModal, openGroupModal, requestClose, requestDeleteUser }}>
      {children}

      {userModalOpen && (
        <UserModal
          isOpen
          user={selectedUser}
          onClose={() => onClose()}
          onCloseConfirmationOpen={() => requestClose('user')}
          onGroupModalOpen={(data) => openGroupModal(data)}
        />
      )}

      {groupModalOpen && selectedUid && (
        <GroupModal isOpen uid={selectedUid} requestClose={() => requestClose('group')} onClose={() => onClose()} />
      )}

      {confirmationOpen && (
        <Portal>
          <ConfirmationPopup
            isOpen
            onCancel={cancelClose}
            onConfirm={confirmClose}
            isLoading={false}
            title={admin_cancel_confirm}
            description={admin_cancel_desc}
            cancelButtonText={admin_no_cancel}
            confirmButtonText={admin_yes_cancel_button}
          />
        </Portal>
      )}

      {deleteConfirmationOpen && selectedUser && (
        <Portal>
          <ConfirmationPopup
            isOpen
            onCancel={cancelDelete}
            onConfirm={confirmDelete}
            isLoading={isDeleteUserLoading}
            title={admin_cancel_desc}
            description={`${admin_delete_user_confirm} ${selectedUser.first_name} ${selectedUser.last_name}?`}
            cancelButtonText={admin_no_cancel}
            confirmButtonText={admin_yes_cancel_button}
          />
        </Portal>
      )}
    </UserModalsContext.Provider>
  )
}
